import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/stores/authStore'
import {
  useCopyCustomView,
  useCreateCustomViewShare,
  useCustomViewShares,
  useDeleteCustomViewShare,
  useUpdateCustomViewShare,
} from '@/hooks/useCustomViewShares'
import { extractApiErrorCode, extractApiErrorStatus } from '@/utils/apiError'
import { refreshSharingState } from '@/services/customViewsSync'
import { useCustomViewNotes } from '@/hooks/useCustomViewNotes'
import { getEmailDomain } from '@/utils/emailDomain'
import { BULK_SHARE_CONFIRM_THRESHOLD, parseRecipients } from '@/utils/shareRecipients'
import type { ApiCustomView, ApiCustomViewShare } from '@/types/api'

type ShareMode = 'access' | 'copy'

const SUBMIT_ERROR_KEYS: Record<string, string> = {
  ShareTargetNotFound: 'customViews.share.targetNotFound',
  ShareTargetAmbiguous: 'customViews.share.targetAmbiguous',
  ShareTargetIsOwner: 'customViews.share.targetIsOwner',
  ShareAlreadyExists: 'customViews.share.alreadyShared',
  SharerUsernameMissing: 'customViews.share.noOwnDomain',
}

interface RecipientFailure {
  address: string
  message: string
}

interface CustomViewShareDialogProps {
  view: ApiCustomView
  open: boolean
  onClose: () => void
}

export function CustomViewShareDialog({ view, open, onClose }: CustomViewShareDialogProps) {
  const { t } = useTranslation()
  const isOffline = useAppStore((s) => s.isOffline)
  const ownUsername = useAuthStore((s) => s.currentUser?.username ?? null)
  const ownDomain = getEmailDomain(ownUsername)

  const [mode, setMode] = useState<ShareMode>('access')
  const [recipientsInput, setRecipientsInput] = useState('')
  const [canEdit, setCanEdit] = useState(false)
  const [submitErrors, setSubmitErrors] = useState<RecipientFailure[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<ApiCustomViewShare | null>(null)

  const sharesQuery = useCustomViewShares(view.id, open && !isOffline)
  const createShare = useCreateCustomViewShare(view.id)
  const updateShare = useUpdateCustomViewShare(view.id)
  const deleteShare = useDeleteCustomViewShare(view.id)
  const copyView = useCopyCustomView(view.id)
  const viewNotes = useCustomViewNotes(view.id, open && !isOffline)
  const queryClient = useQueryClient()

  // 404 from any share call: sharing is off or the view is gone. Close quietly and resync.
  const handleNotFound = (err: unknown): boolean => {
    if (extractApiErrorStatus(err) !== 404) return false
    onClose()
    refreshSharingState(queryClient)
    return true
  }

  const sharesError = sharesQuery.error
  useEffect(() => {
    if (sharesError) handleNotFound(sharesError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharesError])

  const recipients = parseRecipients(recipientsInput)
  const invalidRecipients = recipients.filter((r) => getEmailDomain(r) === null)
  // Outside the sharer's domain the server never says whether an address has an account.
  const externalRecipients = ownDomain === null
    ? []
    : recipients.filter((r) => {
        const domain = getEmailDomain(r)
        return domain !== null && domain !== ownDomain
      })
  const formDisabled = isOffline || ownDomain === null
  const canSubmit = !formDisabled && !submitting && recipients.length > 0
    && invalidRecipients.length === 0

  const handleRecipientsChange = (value: string) => {
    setRecipientsInput(value)
    setSubmitErrors([])
  }

  const failureMessage = (err: unknown) => {
    const key = SUBMIT_ERROR_KEYS[extractApiErrorCode(err) ?? '']
    return key
      ? t(key, { domain: ownDomain ?? '' })
      : t(mode === 'access' ? 'customViews.share.addFailed' : 'customViews.share.copyFailed')
  }

  // One request per recipient, in order. The share and copy endpoints each take a single target,
  // and running them one at a time keeps every failure attributable to its address.
  const shareWithAll = async () => {
    setConfirmBulkOpen(false)
    setSubmitting(true)
    setSubmitErrors([])
    const succeeded: string[] = []
    let sentExternally = 0
    const failed: RecipientFailure[] = []
    try {
      for (const address of recipients) {
        try {
          const external = mode === 'access'
            ? (await createShare.mutateAsync({ username: address, canEdit })).awaitingAcceptance
            : (await copyView.mutateAsync(address)).crossDomain
          if (external) sentExternally++
          else succeeded.push(address)
        } catch (err) {
          if (handleNotFound(err)) return
          failed.push({ address, message: failureMessage(err) })
        }
      }
    } finally {
      setSubmitting(false)
    }

    if (succeeded.length === 1) {
      toast.success(mode === 'access'
        ? t('customViews.share.added')
        : t('customViews.share.copied', { username: succeeded[0] }))
    } else if (succeeded.length > 1) {
      toast.success(mode === 'access'
        ? t('customViews.share.addedCount', { count: succeeded.length })
        : t('customViews.share.copiedCount', { count: succeeded.length }))
    }
    if (sentExternally > 0) {
      toast.info(mode === 'access'
        ? t('customViews.share.sentForAcceptance', { count: sentExternally })
        : t('customViews.share.copySentExternally', { count: sentExternally }))
    }

    // Only the addresses that failed stay in the field, so they can be corrected and resent.
    setRecipientsInput(failed.map((f) => f.address).join(', '))
    setSubmitErrors(failed)
    if (failed.length === 0) setCanEdit(false)
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    if (recipients.length > BULK_SHARE_CONFIRM_THRESHOLD) setConfirmBulkOpen(true)
    else void shareWithAll()
  }

  const hasViewNotes = (view.noteCount ?? 0) > 0
  const handleCopyAllNotes = async () => {
    try {
      const result = await viewNotes.copyAllFromPersonal.mutateAsync()
      toast.success(t('customViews.share.notesCopied', { count: result.copiedCount }))
    } catch (err) {
      if (handleNotFound(err)) return
      toast.error(
        extractApiErrorCode(err) === 'CustomViewNotesExist'
          ? t('customViews.share.notesAlreadyExist')
          : t('customViews.share.notesCopyFailed')
      )
    }
  }

  const handleToggleEdit = async (share: ApiCustomViewShare) => {
    try {
      await updateShare.mutateAsync({ shareId: share.id, canEdit: !share.canEdit })
    } catch (err) {
      if (handleNotFound(err)) return
      toast.error(t('customViews.share.updateFailed'))
    }
  }

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return
    try {
      await deleteShare.mutateAsync(revokeTarget.id)
      toast.success(t('customViews.share.revoked'))
    } catch (err) {
      if (handleNotFound(err)) return
      toast.error(t('customViews.share.revokeFailed'))
    } finally {
      setRevokeTarget(null)
    }
  }

  const shares = sharesQuery.data?.shares ?? []

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{t('customViews.share.title', { name: view.name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                color="primary"
                value={mode}
                onChange={(_, next: ShareMode | null) => {
                  if (next) {
                    setMode(next)
                    setSubmitErrors([])
                  }
                }}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500, py: 0.75 } }}
              >
                <ToggleButton value="access">{t('customViews.share.modeAccess')}</ToggleButton>
                <ToggleButton value="copy">{t('customViews.share.modeCopy')}</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {mode === 'access'
                  ? t('customViews.share.modeAccessHelp')
                  : t('customViews.share.modeCopyHelp', { name: view.name, username: ownUsername ?? '' })}
              </Typography>
            </Box>

            {ownDomain === null && <Alert severity="info">{t('customViews.share.noOwnDomain')}</Alert>}

            <Box
              component="form"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                handleSubmit()
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
                <TextField
                  autoFocus
                  size="small"
                  fullWidth
                  label={t('customViews.share.usernameLabel')}
                  helperText={t('customViews.share.usernameHelp')}
                  value={recipientsInput}
                  onChange={(e) => handleRecipientsChange(e.target.value)}
                  disabled={formDisabled || submitting}
                  error={invalidRecipients.length > 0}
                  slotProps={{ htmlInput: { inputMode: 'email', autoComplete: 'off', maxLength: 4000 } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit}
                  sx={{ flexShrink: 0, alignSelf: 'flex-start', minWidth: 120, height: 40, whiteSpace: 'nowrap' }}
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {mode === 'access' ? t('customViews.share.submit') : t('customViews.share.submitCopy')}
                </Button>
              </Box>
              {mode === 'access' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={canEdit}
                      onChange={(e) => setCanEdit(e.target.checked)}
                      disabled={formDisabled}
                    />
                  }
                  label={t('customViews.share.allowEditing')}
                  slotProps={{ typography: { variant: 'body2' } }}
                  sx={{ mt: 0.5, ml: -0.75 }}
                />
              )}
            </Box>

            {invalidRecipients.length > 0 && (
              <Alert severity="warning">
                {t('customViews.share.invalidAddresses', { list: invalidRecipients.join(', ') })}
              </Alert>
            )}
            {externalRecipients.length > 0 && (
              <Alert severity="info">
                {mode === 'access'
                  ? t('customViews.share.externalNoticeAccess', { domain: ownDomain })
                  : t('customViews.share.externalNoticeCopy', { domain: ownDomain })}
                <Box component="span" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                  {externalRecipients.join(', ')}
                </Box>
              </Alert>
            )}
            {submitErrors.length === 1 && (
              <Alert severity="error">{submitErrors[0].message}</Alert>
            )}
            {submitErrors.length > 1 && (
              <Alert severity="error">
                {t('customViews.share.someFailed', { count: submitErrors.length })}
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {submitErrors.map((f) => (
                    <li key={f.address}>
                      <strong>{f.address}</strong>: {f.message}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            {mode === 'access' && (
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('customViews.share.viewNotes')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {hasViewNotes
                    ? t('customViews.share.viewNotesExist', { count: view.noteCount ?? 0 })
                    : t('customViews.share.viewNotesHelp')}
                </Typography>
                <Tooltip title={hasViewNotes ? t('customViews.share.copyAllNotesBlocked') : ''}>
                  <span>
                    <Button
                      variant="outlined"
                      onClick={handleCopyAllNotes}
                      disabled={isOffline || hasViewNotes || viewNotes.copyAllFromPersonal.isPending}
                      startIcon={
                        viewNotes.copyAllFromPersonal.isPending
                          ? <CircularProgress size={16} color="inherit" />
                          : undefined
                      }
                    >
                      {t('customViews.share.copyAllNotes')}
                    </Button>
                  </span>
                </Tooltip>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('customViews.share.peopleWithAccess')}
                </Typography>
                {sharesQuery.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : shares.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    {t('customViews.share.none')}
                  </Typography>
                ) : (
                  <Paper variant="outlined">
                    {shares.map((share, index) => (
                      <Box
                        key={share.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          pl: 2,
                          pr: 1,
                          py: 0.75,
                          borderTop: index === 0 ? 'none' : '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap title={share.username}>
                            {share.username}
                          </Typography>
                          {share.isPending && (
                            <Chip label={t('customViews.share.pending')} size="small" variant="outlined" />
                          )}
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={share.canEdit}
                              onChange={() => handleToggleEdit(share)}
                              disabled={isOffline || updateShare.isPending}
                            />
                          }
                          label={t('customViews.share.allowEditing')}
                          slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
                          sx={{ mr: 0, flexShrink: 0 }}
                        />
                        <Tooltip title={t('customViews.share.revoke')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setRevokeTarget(share)}
                              disabled={isOffline}
                              aria-label={t('customViews.share.revoke')}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmBulkOpen} onClose={() => setConfirmBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('customViews.share.confirmBulkTitle', { count: recipients.length })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {mode === 'access'
              ? t('customViews.share.confirmBulkAccess', { count: recipients.length, name: view.name })
              : t('customViews.share.confirmBulkCopy', { count: recipients.length, name: view.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmBulkOpen(false)}>{t('notes.cancel')}</Button>
          <Button variant="contained" onClick={() => void shareWithAll()} autoFocus>
            {mode === 'access' ? t('customViews.share.submit') : t('customViews.share.submitCopy')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revokeTarget !== null} onClose={() => !deleteShare.isPending && setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('customViews.share.revoke')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('customViews.share.confirmRevoke', { username: revokeTarget?.username ?? '', name: view.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeTarget(null)} disabled={deleteShare.isPending}>
            {t('notes.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={handleRevokeConfirm} disabled={deleteShare.isPending}>
            {t('customViews.share.revoke')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
